import { useCallback, useRef } from "react";
import { sendEventThunk } from "../../conversation/conversationSlice";
import { useAppDispatch } from "../../hook";
import shouldEmitGreetingImpression, { normalizeGreetingConversationKey } from "./shouldEmitGreetingImpression";
import { GREETING_AUDIO_EVENT_NAME } from "./types";
/**
 * Emits the client-side `greeting_audio` analytics events through the backend `/event` endpoint.
 *
 * Both events describe the static greeting preview only; the live TTS route reports nothing.
 * Identity fields (`user.user_id` / `user.guest_id`) are attached server-side, so the client only
 * sends the event-specific fields. Emits are fire-and-forget and swallow failures so a broken
 * analytics call can never interrupt playback.
 *
 * Impressions are deduplicated against the previous greeting view: one impression fires per new
 * view, and the only suppressed change is an unsaved conversation being persisted (see
 * `shouldEmitGreetingImpression`). The thunk is dispatched directly (rather than through the shared
 * `useEvent` hook) so `dispatch` stays referentially stable and the returned emitters do not force
 * dependent effects to re-run on every render.
 */
export default function useGreetingAudioEvents() {
    const dispatch = useAppDispatch();
    const refLastView = useRef(null);
    const emitImpression = useCallback(({ characterId, conversationKey, voiceId }) => {
        if (!characterId) {
            return;
        }
        const nextView = { characterId, conversationKey: normalizeGreetingConversationKey(conversationKey) };
        const willEmit = shouldEmitGreetingImpression({ previousView: refLastView.current, nextView });
        // Always advance the baseline, even when suppressing, so a persisted id becomes the reference
        // point and a later "start new chat" back to a virtual key is detected as a new view.
        refLastView.current = nextView;
        if (!willEmit) {
            return;
        }
        const eventData = { action: "impression", character_id: characterId };
        if (voiceId) {
            eventData["voice_id"] = voiceId;
        }
        dispatch(sendEventThunk({ event_name: GREETING_AUDIO_EVENT_NAME, ...eventData }))
            .unwrap()
            .catch(() => undefined);
    }, [dispatch]);
    const emitPlay = useCallback(({ characterId, voiceId }) => {
        if (!characterId) {
            return;
        }
        const eventData = { action: "play", character_id: characterId };
        if (voiceId) {
            eventData["voice_id"] = voiceId;
        }
        dispatch(sendEventThunk({ event_name: GREETING_AUDIO_EVENT_NAME, ...eventData }))
            .unwrap()
            .catch(() => undefined);
    }, [dispatch]);
    return { emitImpression, emitPlay };
}
